import React, { useEffect, useState } from 'react';
import { Button } from '@this/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio';
import { Textarea } from '@this/shared/ui/inputs/textarea';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { BiztraModal, BiztraModalHeader, BiztraModalBody } from '../../ui/feedbacks/modal';
import CardSetupForm from '../CardSetupForm';
import type { ChurnReasonCode, PlanType } from './plan_types';
import { CHURN_REASON_OPTIONS } from './plan_types';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  selectedPlanType: PlanType;
  memberCount: number;
  standardPricePerUser: number;
  proPricePerUser: number;
  currentPlanType: PlanType | null;
  stripePromise: Promise<Stripe | null> | null;
  onPaymentSucceeded: () => void;
}

export const PlanUpgradeModal = ({
  open,
  onClose,
  selectedPlanType,
  memberCount,
  standardPricePerUser,
  proPricePerUser,
  currentPlanType,
  stripePromise,
  onPaymentSucceeded
}: UpgradeModalProps) => {
  const styles = getStyles();
  const selectedPlanPrice = selectedPlanType === 'pro' ? proPricePerUser : standardPricePerUser;
  return (
    <BiztraModal size="medium" onClose={onClose} open={open} disableEnforceFocus>
      <BiztraModalHeader>
        {selectedPlanType === 'standard' ? 'AI Expense Standard' : 'AI Expense Pro'} にアップグレード
      </BiztraModalHeader>
      <BiztraModalBody>
        <div style={styles.modalBody}>
          <div style={styles.description}>
            <div>現在のユーザ数：{memberCount}人</div>
            <div>
              月額料金：<span style={styles.price}>{utils.formatPrice(selectedPlanPrice * memberCount)}</span>
              （税込）
            </div>
            {currentPlanType === 'standard' && selectedPlanType === 'pro' && (
              <div>※スタンダードプランとの差額（残り日数に応じた日割り）を請求します</div>
            )}
            <div>※翌月以降は自動更新されます</div>
          </div>
          <div style={styles.creditCardInputWrap}>
            <Elements stripe={stripePromise}>
              <CardSetupForm handleSucceeded={onPaymentSucceeded} planType={selectedPlanType} />
            </Elements>
          </div>
        </div>
      </BiztraModalBody>
    </BiztraModal>
  );
};

interface DowngradeModalProps {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  error: string | null;
  onConfirm: () => void;
}

export const PlanDowngradeModal = ({ open, onClose, submitting, error, onConfirm }: DowngradeModalProps) => {
  const styles = getStyles();
  return (
    <BiztraModal size="large" onClose={onClose} open={open}>
      <BiztraModalHeader>AI Expense Standard にダウングレード</BiztraModalHeader>
      <BiztraModalBody>
        <div style={styles.modalBody}>
          <div style={styles.section}>
            <div>次回更新時に AI Expense Standard にダウングレードしますか？</div>
            <div>※現在の有効期限まで Pro プランのままご利用いただけます</div>
            <div>※次回更新日以降、OCR機能は利用できなくなります</div>
            <div>※ダウングレードの取り消しは、次回更新日より前であればいつでも可能です</div>
          </div>
          <div style={styles.buttonArea}>
            <Link onClick={onClose}>戻る</Link>
            {submitting ? (
              <div style={styles.rightElement}>
                <Loading />
              </div>
            ) : (
              <Button style={styles.rightButton} onClick={onConfirm}>
                予約する
              </Button>
            )}
          </div>
          <div style={styles.error}>{error}</div>
        </div>
      </BiztraModalBody>
    </BiztraModal>
  );
};

interface CancelDowngradeModalProps {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  error: string | null;
  onConfirm: () => void;
}

export const PlanCancelDowngradeModal = ({
  open,
  onClose,
  submitting,
  error,
  onConfirm
}: CancelDowngradeModalProps) => {
  const styles = getStyles();
  return (
    <BiztraModal size="large" onClose={onClose} open={open}>
      <BiztraModalHeader>ダウングレード予定を取り消す</BiztraModalHeader>
      <BiztraModalBody>
        <div style={styles.modalBody}>
          <div style={styles.section}>
            <div>ダウングレードの予定を取り消しますか？</div>
            <div>※取り消し後も引き続き Pro プランをご利用いただけます</div>
          </div>
          <div style={styles.buttonArea}>
            <Link onClick={onClose}>戻る</Link>
            {submitting ? (
              <div style={styles.rightElement}>
                <Loading />
              </div>
            ) : (
              <Button style={styles.rightButton} onClick={onConfirm}>
                取り消す
              </Button>
            )}
          </div>
          <div style={styles.error}>{error}</div>
        </div>
      </BiztraModalBody>
    </BiztraModal>
  );
};

interface CancelModalProps {
  open: boolean;
  onClose: () => void;
  currentPlan: string | null;
  submitting: boolean;
  error: string | null;
  onConfirm: () => void;
}

export const PlanCancelModal = ({
  open,
  onClose,
  currentPlan,
  submitting,
  error,
  onConfirm
}: CancelModalProps) => {
  const styles = getStyles();
  return (
    <BiztraModal size="large" onClose={onClose} open={open}>
      <BiztraModalHeader>サブスクリプションをキャンセル</BiztraModalHeader>
      <BiztraModalBody>
        <div style={styles.modalBody}>
          <div style={styles.section}>
            <div>{currentPlan}のサブスクリプションをキャンセルしますか？</div>
            <div>※変更後も、現在のプランの有効期限まではご利用いただけます</div>
            <div>※クレジットポイント残高がプラスの場合は、キャンセルした時点で破棄されます</div>
            <div>
              ※クレジットポイント残高がマイナスの場合は、キャンセルした時点で不足分がクレジットカードから引き落とされます
            </div>
          </div>
          <div style={styles.buttonArea}>
            <Link onClick={onClose}>戻る</Link>
            {submitting ? (
              <div style={styles.rightElement}>
                <Loading />
              </div>
            ) : (
              <Button style={styles.rightButton} onClick={onConfirm}>
                はい
              </Button>
            )}
          </div>
          <div style={styles.error}>{error}</div>
        </div>
      </BiztraModalBody>
    </BiztraModal>
  );
};

interface ChurnModalProps {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  error: string | null;
  onConfirm: (reasonCode: ChurnReasonCode, reasonDetail: string | null) => void;
}

export const PlanChurnModal = ({ open, onClose, submitting, error, onConfirm }: ChurnModalProps) => {
  const styles = getStyles();
  const [reasonCode, setReasonCode] = useState<ChurnReasonCode | ''>('');
  const [reasonDetail, setReasonDetail] = useState('');

  useEffect(() => {
    if (open) {
      setReasonCode('');
      setReasonDetail('');
    }
  }, [open]);

  const canConfirm = reasonCode !== '' && (reasonCode !== 'other' || reasonDetail.trim() !== '') && !submitting;

  const handleConfirm = () => {
    if (reasonCode === '' || submitting || (reasonCode === 'other' && reasonDetail.trim() === '')) {
      return;
    }
    onConfirm(reasonCode, reasonCode === 'other' ? reasonDetail.trim() : null);
  };

  return (
    <BiztraModal size="large" onClose={onClose} open={open}>
      <BiztraModalHeader>解約手続き</BiztraModalHeader>
      <BiztraModalBody>
        <div style={styles.modalBody}>
          <div style={styles.section}>
            <div style={styles.churnTitle}>本当に解約してよろしいですか？</div>
            <div>解約を確定すると、以下の内容が適用されます。</div>
            <ul style={styles.churnList}>
              <li>解約確定後、即時に管理者・全ユーザーともにログインができなくなります</li>
              <li>過去のご利用データは閲覧できなくなりますので、あらかじめデータのダウンロードをお願いします</li>
              <li>一度解約すると、同一企業として再度ご利用いただく場合は新規お申し込みが必要です</li>
            </ul>
            <div>ご了承の上、解約理由をご回答ください。</div>
          </div>
          <div style={styles.section}>
            <div style={styles.churnReasonLabel}>解約理由（※必須項目）</div>
            <RadioGroup
              value={reasonCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setReasonCode(e.target.value as ChurnReasonCode)
              }
            >
              {CHURN_REASON_OPTIONS.map(option => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
            {reasonCode === 'other' && (
              <Textarea
                value={reasonDetail}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReasonDetail(e.target.value)}
                placeholder="解約理由をご入力ください"
                fullWidth
                rowsMin={3}
                style={styles.churnReasonDetail}
              />
            )}
          </div>
          <div style={styles.buttonArea}>
            <Link onClick={onClose}>戻る</Link>
            {submitting ? (
              <div style={styles.rightElement}>
                <Loading />
              </div>
            ) : (
              <Button style={styles.rightButton} onClick={handleConfirm} disabled={!canConfirm}>
                解約を確定する
              </Button>
            )}
          </div>
          <div style={styles.error}>{error}</div>
        </div>
      </BiztraModalBody>
    </BiztraModal>
  );
};

function getStyles() {
  const buttonBase: React.CSSProperties = { width: 'fit-content', borderRadius: '4px', marginBottom: '12px' };
  return {
    creditCardInputWrap: {
      minWidth: '550px',
      marginTop: '24px',
      paddingTop: '32px',
      borderTop: 'solid 1px #eee'
    } as React.CSSProperties,
    modalBody: { margin: '0 auto', width: 'fit-content' } as React.CSSProperties,
    section: { marginBottom: '40px' } as React.CSSProperties,
    description: { marginBottom: '20px' } as React.CSSProperties,
    buttonArea: { display: 'flex', alignItems: 'center' } as React.CSSProperties,
    rightButton: { ...buttonBase, marginLeft: 'auto' } as React.CSSProperties,
    rightElement: { marginLeft: 'auto' } as React.CSSProperties,
    price: { color: '#a72c09', fontWeight: 'bold' } as React.CSSProperties,
    error: { color: '#a72c09' } as React.CSSProperties,
    churnTitle: { fontWeight: 'bold', marginBottom: '8px' } as React.CSSProperties,
    churnList: {
      listStyleType: 'disc',
      paddingLeft: '25px',
      margin: '12px 0'
    } as React.CSSProperties,
    churnReasonLabel: { fontWeight: 'bold', marginBottom: '12px' } as React.CSSProperties,
    churnReasonDetail: { marginTop: '12px' } as React.CSSProperties
  } as const;
}
